import React, { useMemo } from "react";
import { Navigate, Outlet, useLocation, useParams, useSearchParams } from "react-router";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { Spin } from "antd";

type Props = {
  publicOnly?: boolean;
  onlyAvailableForSSOCompanion?: boolean;
};

const SSOGuard = ({ publicOnly = false, onlyAvailableForSSOCompanion = false }: Props) => {
  const ssoModule = getModule("sso");
  const isCompanionSso = useMemo(() => ssoModule?.enabled, []);
  const isCompanionSsoOnly = useMemo(() => ssoModule?.options?.ssoOnly, []);

  const params = useParams();
  const [searchParams] = useSearchParams();
  const ssoAuthCode = useMemo(
    () => searchParams.get("code") ?? params?.token,
    [params, searchParams]
  );
  const { pathname } = useLocation();

  const ssoAuthorizeUrl = useMemo(() => getModule("sso")?.options?.ssoAuthorizeUrl ?? "", []);
  const ssoAuthorizeUrlWithParams = useMemo(() => {
    if (!ssoAuthorizeUrl) return "";
    try {
      const url = new URL(ssoAuthorizeUrl);
      url.searchParams.set("state", pathname);
      url.searchParams.set("redirect_uri", window.location.origin + "/");
      return url.toString();
    } catch {
      const hasParams = ssoAuthorizeUrl.includes("?");
      const separator = hasParams ? "&" : "?";
      const encodedPathName = encodeURIComponent(pathname);
      const encodedRedirectUri = encodeURIComponent(window.location.origin + "/");
      return `${ssoAuthorizeUrl}${separator}state=${encodedPathName}&redirect_uri=${encodedRedirectUri}`;
    }
  }, []);

  if (onlyAvailableForSSOCompanion && !isCompanionSso) {
    return <Navigate to="/not-found" />;
  }
  if (
    isCompanionSso &&
    ssoAuthorizeUrl &&
    !ssoAuthCode &&
    pathname !== "/sso/error" &&
    pathname !== "/sso/no-rights"
  ) {
    window.open(ssoAuthorizeUrlWithParams, "_self");
    return <Spin className="sso-consent__spin" size="large" />;
  }
  if (publicOnly && isCompanionSsoOnly) {
    return <Navigate to="/sso/no-rights" />;
  }
  return <Outlet />;
};

export default SSOGuard;
